/* eslint-disable no-console */
import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Drawer from '@mui/material/Drawer';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Controller, useForm, useWatch } from 'react-hook-form';
import PropTypes from 'prop-types';
import DrawerHeader from '../DrawerHeader';
import DrawerBody from '../DrawerBody';
import DrawerFooter from '../DrawerFooter';
import DrawerMessage from '../DrawerMessage';
import EmailRedirectHint from '../EmailRedirectHint';
import FormSubmitOverlay, {
	FORM_SUBMIT_OVERLAY_MODES,
} from '../FormSubmitOverlay';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import { useEmailService } from '../../services/email/useEmailService';
import { isRequiredTrimmed } from '../../utils/validation';
import { ContactUsRequestTemplate } from '../../utils/forms';

const DEFAULT_FORM_VALUES = {
	cc: '',
	message: '',
};

function ContactUsDrawer({
	isOpen,
	onClose,
	from,
	to,
	toDisplay,
	subject,
	locales,
	firstName,
	lastName,
}) {
	const { t } = useTranslation('contact_us');
	const isMountedRef = useIsMountedRef();
	const [alert, setAlert] = useState('');
	const [error, setError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isMobileView, setIsMobileView] = useState(false);
	const [isAlertVisible, setIsAlertVisible] = useState(false);
	const { control, handleSubmit, reset } = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: DEFAULT_FORM_VALUES,
	});
	const messageValue = useWatch({
		control,
		name: 'message',
	});
	const isSubmitDisabled = !isRequiredTrimmed(messageValue) || isSubmitting;
	const displayTo = toDisplay || to;
	const { sendEmail, actualUserEmail, isEmailRedirectEnabled } =
		useEmailService();
	const showRecipientRedirectHint =
		isEmailRedirectEnabled && Boolean(actualUserEmail);

	const checkIsMobileView = () => {
		if (window.innerWidth < 768) {
			setIsMobileView(true);
		} else {
			setIsMobileView(false);
		}
	};

	useEffect(() => {
		checkIsMobileView();
		window.addEventListener('resize', checkIsMobileView);
		return () => window.removeEventListener('resize', checkIsMobileView);
	}, []);

	const sendContactUsEmail = useCallback(
		async (values) => {
			if (!isMountedRef.current) {
				return;
			}

			setIsSubmitting(true);

			const trimmedSubject = subject ? subject.trim() : '';
			const trimmedMessage = values.message ? values.message.trim() : '';

			const response = await sendEmail({
				to,
				cc: values.cc,
				locale: locales,
				templateName: ContactUsRequestTemplate(),
				customized: {
					submittedByFirstName: firstName,
					submittedByLastName: lastName,
					replyToEmail: from,
					recipientName: toDisplay,
					reference: trimmedSubject,
					message: trimmedMessage,
				},
			});

			setIsSubmitting(false);

			if (response?.isAborted) {
				return;
			}

			if (response?.isError) {
				setAlert('');
				setError(t('error'));
				setIsAlertVisible(true);
				setTimeout(() => {
					setIsAlertVisible(false);
				}, 4000);
				return;
			}

			if (response) {
				setError('');
				setAlert(t('alert'));
				setIsAlertVisible(true);
				reset(DEFAULT_FORM_VALUES);
				setTimeout(() => {
					setIsAlertVisible(false);
				}, 4000);
				setTimeout(onClose, 6000);
			}
		},
		[
			isMountedRef,
			to,
			toDisplay,
			locales,
			from,
			subject,
			firstName,
			lastName,
			sendEmail,
			onClose,
			reset,
			t,
			setAlert,
			setError,
			setIsSubmitting,
		]
	);

	function validateRequiredTrimmed(value) {
		return isRequiredTrimmed(value) ? true : t('blank');
	}

	function renderFieldLabel(label, required = false) {
		return (
			<Typography
				variant="body2"
				sx={{ mb: 0.75, fontWeight: 500, fontSize: '0.85rem' }}
			>
				{label}
				{required && (
					<Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
						*
					</Box>
				)}
			</Typography>
		);
	}

	return !isOpen ? null : (
		<Drawer
			open
			onClose={onClose}
			anchor="right"
			variant="temporary"
			locale={locales}
			PaperProps={{
				sx: {
					width: { xs: '100vw', sm: 680 },
					maxWidth: '100vw',
				},
			}}
		>
			<DrawerHeader
				onClose={onClose}
				title={!isMobileView ? t('title') : null}
			/>

			<form
				onSubmit={handleSubmit(sendContactUsEmail)}
				style={{
					display: 'flex',
					flexDirection: 'column',
					flex: 1,
					minHeight: 0,
				}}
			>
				<DrawerMessage
					message={isAlertVisible && error ? t('error') : ''}
					severity="error"
				/>
				<DrawerMessage
					message={isAlertVisible && alert ? t('alert') : ''}
					severity="success"
				/>

				<DrawerBody sx={{ pt: 2 }}>
					<Typography
						variant="body2"
						color="textSecondary"
						sx={{ mb: 3, lineHeight: 1.25 }}
						aria-label={t('subtitle_order_details')}
					>
						{t('subtitle_order_details')}
					</Typography>

					<FormControl
						variant="outlined"
						fullWidth
						data-testid="contact_us_drawer_form"
					>
						<Grid item aria-label={t('email_fields.to')}>
							{renderFieldLabel(t('email_fields.to'))}
							<TextField
								fullWidth
								id="to"
								variant="outlined"
								value={displayTo}
								size="small"
								margin="none"
								onMouseDown={(event) => event.preventDefault()}
								onFocus={(event) => event.target.blur()}
								inputProps={{
									readOnly: true,
									tabIndex: -1,
									'aria-label': t('email_fields.to'),
								}}
							/>
						</Grid>
						<Grid item sx={{ mt: 1 }} aria-label={t('email_fields.from')}>
							{renderFieldLabel(t('email_fields.from'))}
							<TextField
								fullWidth
								id="from"
								value={from}
								variant="outlined"
								size="small"
								margin="none"
								onMouseDown={(event) => event.preventDefault()}
								onFocus={(event) => event.target.blur()}
								inputProps={{
									readOnly: true,
									tabIndex: -1,
									'aria-label': t('email_fields.from'),
								}}
							/>
						</Grid>
						<Grid item sx={{ mt: 1 }} aria-label={t('email_fields.cc')}>
							{renderFieldLabel(t('email_fields.cc'))}
							<Controller
								name="cc"
								control={control}
								render={({ field }) => (
									<TextField
										fullWidth
										id="cc"
										variant="outlined"
										size="small"
										margin="none"
										{...field}
										inputProps={{
											'aria-label': t('email_fields.cc'),
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item sx={{ mt: 1 }}>
							{renderFieldLabel(t('email_fields.subject'))}
							<TextField
								fullWidth
								id="subject"
								value={subject}
								variant="outlined"
								size="small"
								margin="none"
								onMouseDown={(event) => event.preventDefault()}
								onFocus={(event) => event.target.blur()}
								inputProps={{
									readOnly: true,
									tabIndex: -1,
									'aria-label': t('email_fields.subject'),
								}}
							/>
						</Grid>

						<Grid item sx={{ mt: 4 }}>
							{renderFieldLabel(t('email_fields.message'), true)}
							<Controller
								name="message"
								control={control}
								rules={{ validate: validateRequiredTrimmed }}
								render={({ field, fieldState }) => (
									<TextField
										fullWidth
										id="message"
										role="textbox"
										variant="outlined"
										size="small"
										margin="none"
										multiline
										rows={12}
										{...field}
										error={Boolean(fieldState.error)}
										helperText={
											fieldState.error ? fieldState.error.message : ''
										}
										inputProps={{
											'aria-label': 'contact-us-drawer-text',
										}}
										sx={{
											'& .MuiInputBase-inputMultiline': {
												resize: 'vertical',
											},
										}}
									/>
								)}
							/>
						</Grid>
					</FormControl>
				</DrawerBody>

				<DrawerFooter divider>
					<Grid container justifyContent="center">
						<Grid item>
							<Button
								variant="contained"
								color="primary"
								size="large"
								disabled={isSubmitDisabled}
								fullWidth={isMobileView}
								sx={{ minWidth: 150 }}
								role="button"
								type="submit"
								data-testid={t('contact_btn_send')}
								aria-label={t('contact_btn_send')}
							>
								<EmailRedirectHint
									show={showRecipientRedirectHint}
									actualUserEmail={actualUserEmail}
									inline
								/>{' '}
								{t('submit')}
							</Button>
						</Grid>
					</Grid>
				</DrawerFooter>
			</form>
			<FormSubmitOverlay
				open={isSubmitting}
				mode={FORM_SUBMIT_OVERLAY_MODES.PAGE_FORM}
			/>
		</Drawer>
	);
}

ContactUsDrawer.propTypes = {
	from: PropTypes.string,
	to: PropTypes.string,
	toDisplay: PropTypes.string,
	cc: PropTypes.string,
	subject: PropTypes.string,
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	locales: PropTypes.string.isRequired,
	firstName: PropTypes.string,
	lastName: PropTypes.string,
};
export default ContactUsDrawer;
